import { useState } from "react";
import { Copy, ArrowDownToLine, ArrowUpFromLine, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const BotWalletCard = ({ wallet, phantom, refresh, me }) => {
  const [amount, setAmount] = useState("0.1");
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(wallet.address);
    toast.success("Bot wallet address copied");
  };

  const fund = async () => {
    setBusy(true);
    try {
      const sig = await phantom.fund(wallet.address, parseFloat(amount));
      toast.success(`Funding sent: ${sig.slice(0, 10)}…`);
      setTimeout(refresh, 6000);
    } catch (e) {
      toast.error(e.message || "Funding failed");
    }
    setBusy(false);
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/withdraw", me.wallet ? {} : { to: dest });
      toast.success(`Withdrew ${data.sol.toFixed(4)} SOL`);
      setTimeout(refresh, 6000);
    } catch (e) {
      toast.error(errMsg(e, "Withdraw failed"));
    }
    setBusy(false);
  };

  return (
    <div className="panel p-5 rise" data-testid="bot-wallet-card">
      <div className="flex items-center justify-between">
        <div className="panel-title">Dedicated bot wallet</div>
        <a href={`https://solscan.io/account/${wallet.address}`} target="_blank" rel="noreferrer" data-testid="bot-wallet-solscan"
          className="text-slate-500 hover:text-[#14F195] transition-colors"><ExternalLink className="w-3.5 h-3.5" /></a>
      </div>
      <button onClick={copy} data-testid="bot-wallet-address"
        className="mt-4 w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-[#07090D] border border-[#1E293B] hover:border-[#14F195]/50 transition-colors font-mono text-[11px] text-slate-300 break-all text-left">
        {wallet.address} <Copy className="w-3.5 h-3.5 shrink-0 text-slate-500" />
      </button>
      <p className="mt-3 text-xs text-slate-500 leading-relaxed">
        Signs swaps 24/7 so you never have to approve trades. Key is encrypted on the server. Fund it with a small amount only.
      </p>
      <div className="mt-4 flex gap-2">
        <div className="flex items-center border border-[#1E293B] bg-[#07090D] flex-1">
          <input data-testid="fund-amount-input" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-transparent px-3 py-2 font-mono text-sm outline-none" />
          <span className="pr-3 text-xs font-mono text-slate-500">SOL</span>
        </div>
        <button data-testid="fund-bot-btn" onClick={fund} disabled={busy || !(parseFloat(amount) > 0)}
          className="flex items-center gap-1.5 px-4 bg-[#14F195] text-[#07090D] text-xs font-bold hover:bg-[#0fd884] disabled:opacity-40 active:scale-[0.97] transition-[background-color,transform]">
          <ArrowDownToLine className="w-3.5 h-3.5" /> Fund
        </button>
      </div>
      {!me.wallet && (
        <input data-testid="withdraw-address-input" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Withdraw to (your Solana address)"
          className="mt-4 w-full bg-[#07090D] border border-[#1E293B] px-3 py-2 text-xs font-mono outline-none focus:border-[#14F195]/60 transition-colors" />
      )}
      <button data-testid="withdraw-bot-btn" onClick={withdraw} disabled={busy || (!me.wallet && !dest.trim())}
        className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 border border-[#1E293B] text-xs text-slate-300 hover:border-[#334155] hover:bg-[#111827] disabled:opacity-40 transition-colors">
        <ArrowUpFromLine className="w-3.5 h-3.5" /> Withdraw all SOL{me.wallet ? " to your Phantom" : ""}
      </button>
      <p className="mt-2 text-[11px] font-mono text-slate-500" data-testid="connect-hint">Funding opens Phantom in your browser to approve the transfer.</p>
    </div>
  );
};
